import { Link } from '@/i18n/navigation'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'

export interface ContentRefListItemProps {
  /** Locale-aware destination, already resolved (e.g. "/tutorials/box-tie"). */
  href: string
  title: string
  /** Raw Sanity CDN image URL (no query params) — sized via `?w=…` here. */
  thumbnailUrl: string | null
  /**
   * Thumbnail scale. `sm` = compact rail card (36px thumb) for desktop
   * sidebars; `md` = slightly larger (40px) for mobile drawers.
   */
  size?: 'sm' | 'md'
}

/**
 * A horizontal "content reference" card — small square thumbnail beside a
 * single-line truncated title, linking to another piece of content.
 *
 * This is the canonical Prerequisites / "More Like This" rail card. It was
 * duplicated inline several times (four copies in tutorial-view.tsx plus one
 * in scene-view.tsx, each drifting slightly); this component is the single
 * source of truth so every content page renders the same card with the same
 * hover (border → rope accent, animated via `transition-colors`).
 */
export function ContentRefListItem({
  href,
  title,
  thumbnailUrl,
  size = 'sm',
}: ContentRefListItemProps) {
  const thumb = size === 'md' ? 'h-10 w-10' : 'h-9 w-9'
  const px = size === 'md' ? 80 : 72 // 2× the css thumb size for retina
  const titleSize = size === 'md' ? 'text-sm' : 'text-[13px]'
  return (
    <Link
      href={href}
      className="flex items-center gap-3 border border-border bg-card p-2.5 text-card-foreground transition-colors hover:border-rope"
    >
      <span
        className={`${thumb} shrink-0 overflow-hidden`}
        style={{ background: 'color-mix(in srgb, var(--rope-light) 30%, var(--muted))' }}
      >
        {thumbnailUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={optimizeSanityImageUrl(thumbnailUrl, px, { height: px, fit: 'crop' })}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : null}
      </span>
      <span className={`block min-w-0 truncate ${titleSize} font-medium leading-tight`}>
        {title}
      </span>
    </Link>
  )
}
